import { useMemo, useState } from 'react';
import type { Reservation } from '@vinz/shared';
import { SERVICES } from '@vinz/shared';
import { useAuth } from '../auth.js';
import { formatDuration, formatLocal, viewerTimeZone } from '../lib/format.js';
import { useHistoryQuery } from '../use-api.js';

// Mirrors packages/api/src/handlers/history.ts's DemandRow. Not published from @vinz/shared, so it
// is redeclared here rather than imported across the Lambda/portal boundary. `extensions` is counted
// in the roll-up itself (Store.recordHold ADDs it alongside holds/heldSeconds/holders) and summed
// there by the handler, the same way holds and heldSeconds are - never approximated client-side from
// the capped holds sample, which cannot be reconstructed once individual hold rows TTL out.
export interface DemandRow {
  repo: string;
  holds: number;
  people: number;
  occupancyPct: number;
  meanHoldSeconds: number;
  lanesCandidate: boolean;
  extensions: number;
}

export interface HistoryResponse {
  demand: DemandRow[];
  holds: Record<string, unknown>[];
}

// The lanes decision (00-reserved-region-model.md §10) is Dave's call, made off the API's own
// `lanesCandidate` flag. The 80% threshold that sets that flag lives in exactly one place -
// LANES_OCCUPANCY_THRESHOLD in packages/api/src/handlers/history.ts - and is never re-derived here.

const PERIODS = [
  { value: '90d', label: 'Last 90 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
] as const;

type Period = (typeof PERIODS)[number]['value'];

const DAY_MS = 24 * 3600 * 1000;

// "All time" has to name a start. Omitting `from` is not the same thing: the API reads a missing
// `from` as its own 90-day default, which made "All time" and "Last 90 days" return byte-identical
// results. The epoch is earlier than any roll-up can be, and the API clamps the window it divides by
// to the earliest month it actually recorded, so the occupancy column stays meaningful.
const ALL_TIME_FROM = new Date(0).toISOString();

function periodFrom(period: Period, nowMs: number): string {
  switch (period) {
    case '90d':
      return new Date(nowMs - 90 * DAY_MS).toISOString();
    case '30d':
      return new Date(nowMs - 30 * DAY_MS).toISOString();
    case '12m':
      return new Date(nowMs - 365 * DAY_MS).toISOString();
    case 'all':
    default:
      return ALL_TIME_FROM;
  }
}

interface HoldView {
  id: string;
  repo: string;
  who: string;
  reason: string;
  startedIso?: string;
  durationSeconds?: number;
  extensions: number;
}

/**
 * A hold row from the API is `Record<string, unknown>` - it is a deployment-history item that
 * optionally carries the reservation payload that was live at the time. Field access is defensive
 * throughout: `actor`/`deployedAt` are the deploy-observation fallback, `reservation.*` is the
 * richer, hold-specific detail when present.
 */
function toHoldView(row: Record<string, unknown>, index: number): HoldView {
  const repo = typeof row.repo === 'string' ? row.repo : '';
  const workspace = typeof row.workspace === 'string' ? row.workspace : '';
  const reservation = row.reservation as Partial<Reservation> | undefined;
  const actor = typeof row.actor === 'string' ? row.actor : undefined;
  const deployedAt = typeof row.deployedAt === 'string' ? row.deployedAt : undefined;

  const who = reservation?.holder ?? actor ?? '—';
  const reason = reservation?.reason ?? '—';
  const startedIso = reservation?.granted_at ?? deployedAt;
  const durationSeconds =
    reservation?.granted_at && reservation?.expires_at
      ? Math.round((Date.parse(reservation.expires_at) - Date.parse(reservation.granted_at)) / 1000)
      : undefined;

  return {
    id: `${repo}#${workspace}#${startedIso ?? index}`,
    repo,
    who,
    reason,
    startedIso,
    durationSeconds,
    extensions: reservation?.extensions ?? 0,
  };
}

function distinctPeople(holds: Record<string, unknown>[]): string[] {
  const set = new Set<string>();
  holds.forEach((row, i) => {
    const { who } = toHoldView(row, i);
    if (who !== '—') set.add(who);
  });
  return [...set].sort();
}

function overallMeanHoldSeconds(demand: DemandRow[]): number {
  let totalHeldSeconds = 0;
  let totalHolds = 0;
  for (const d of demand) {
    totalHeldSeconds += d.meanHoldSeconds * d.holds;
    totalHolds += d.holds;
  }
  return totalHolds > 0 ? Math.round(totalHeldSeconds / totalHolds) : 0;
}

function OccupancyCell({ pct }: { pct: number }) {
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <div className="occupancy-cell">
      <div
        className="progress-bar progress-bar-brand"
        role="progressbar"
        aria-valuenow={Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="progress-bar-fill" style={{ width: `${clamped}%` }} />
      </div>
      <span>{pct}%</span>
    </div>
  );
}

export interface DemandTableProps {
  demand: DemandRow[];
}

/** The main table: which services are contended, led by occupancy. Plain props - no network, no auth. */
export function DemandTable({ demand }: DemandTableProps) {
  return (
    <table>
      <thead>
        <tr>
          <th scope="col">Service</th>
          <th scope="col">Holds</th>
          <th scope="col">People</th>
          <th scope="col">Occupancy</th>
          <th scope="col">Mean hold</th>
          <th scope="col">Extensions</th>
        </tr>
      </thead>
      <tbody>
        {demand.map((row) => (
          <tr key={row.repo}>
            <td>
              {row.repo}
              {row.lanesCandidate && <div className="lanes-badge">lanes candidate</div>}
            </td>
            <td>{row.holds}</td>
            <td>{row.people}</td>
            <td>
              <OccupancyCell pct={row.occupancyPct} />
            </td>
            <td>{formatDuration(row.meanHoldSeconds)}</td>
            <td>{row.extensions}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HoldsTable({ holds }: { holds: Record<string, unknown>[] }) {
  const rows = holds.map((row, i) => toHoldView(row, i));
  return (
    <table>
      <thead>
        <tr>
          <th scope="col">Service</th>
          <th scope="col">Who</th>
          <th scope="col">Reason</th>
          <th scope="col">Started</th>
          <th scope="col">Duration</th>
          <th scope="col">Extensions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td>{row.repo}</td>
            <td>{row.who}</td>
            <td>{row.reason}</td>
            <td>{row.startedIso ? formatLocal(row.startedIso) : '—'}</td>
            <td>{row.durationSeconds !== undefined ? formatDuration(row.durationSeconds) : '—'}</td>
            <td>{row.extensions}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SummaryTiles({ demand, holds }: { demand: DemandRow[]; holds: Record<string, unknown>[] }) {
  const holdsGranted = demand.reduce((sum, d) => sum + d.holds, 0);
  const peopleCount = distinctPeople(holds).length;
  const everHeld = demand.filter((d) => d.holds > 0).length;
  const meanHold = overallMeanHoldSeconds(demand);
  return (
    <div className="tile-row">
      <div className="tile">
        <div className="tile-value">{holdsGranted}</div>
        <div className="tile-label">Holds granted</div>
      </div>
      <div className="tile">
        <div className="tile-value">{peopleCount}</div>
        <div className="tile-label">Distinct people</div>
      </div>
      <div className="tile">
        <div className="tile-value">{everHeld} of {SERVICES.length} tracked</div>
        <div className="tile-label">Services ever held</div>
      </div>
      <div className="tile">
        <div className="tile-value">{formatDuration(meanHold)}</div>
        <div className="tile-label">Mean hold</div>
      </div>
    </div>
  );
}

function LanesCallout({ demand }: { demand: DemandRow[] }) {
  // demand is already sorted by occupancy descending (the API's own ordering, never re-sorted
  // here), so the first lanesCandidate row found is also the most contended one.
  const candidate = demand.find((d) => d.lanesCandidate);
  if (!candidate) return null;
  return (
    <div className="callout callout-warning">
      <strong>{candidate.repo}</strong> is held {candidate.occupancyPct}% of the period - the strongest
      lanes candidate.
    </div>
  );
}

interface FilterBarProps {
  service: string;
  period: Period;
  onServiceChange: (value: string) => void;
  onPeriodChange: (value: Period) => void;
}

function FilterBar({ service, period, onServiceChange, onPeriodChange }: FilterBarProps) {
  return (
    <div className="filter-bar">
      <div className="filter-field">
        <label htmlFor="history-filter-service">Service</label>
        <select
          id="history-filter-service"
          value={service}
          onChange={(e) => onServiceChange(e.target.value)}
        >
          <option value="">All services</option>
          {SERVICES.map((s) => (
            <option key={s.repo} value={s.repo}>{s.repo}</option>
          ))}
        </select>
      </div>
      <div className="filter-field">
        <label htmlFor="history-filter-period">Period</label>
        <select
          id="history-filter-period"
          value={period}
          onChange={(e) => onPeriodChange(e.target.value as Period)}
        >
          {PERIODS.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/**
 * Scoped to the holds table and rendered inside it, not in the filter bar above: the demand table and
 * tiles come from roll-ups, which count holders as a set per month and cannot be split by person. A
 * Person control beside Service and Period read as filtering everything while changing only this.
 */
function PersonFilter({ person, people, onPersonChange }: {
  person: string; people: string[]; onPersonChange: (v: string) => void;
}) {
  return (
    <div className="filter-bar">
      <div className="filter-field">
        <label htmlFor="history-filter-person">Person</label>
        <select
          id="history-filter-person"
          value={person}
          onChange={(e) => onPersonChange(e.target.value)}
        >
          <option value="">Anyone</option>
          {people.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

export function History() {
  const { idToken } = useAuth();
  const [service, setService] = useState('');
  const [period, setPeriod] = useState<Period>('90d');
  const [person, setPerson] = useState('');

  // Memoised on the period: `from` is in the query key, so recomputing it from Date.now() on every
  // render gave every render a new key and started another request - the page could refetch forever.
  const from = useMemo(() => periodFrom(period, Date.now()), [period]);
  const { data, isPending, isError, error } = useHistoryQuery(idToken, { service: service || undefined, from });

  const holds = data?.holds ?? [];
  const people = useMemo(() => distinctPeople(holds), [holds]);
  const filteredHolds = person ? holds.filter((row, i) => toHoldView(row, i).who === person) : holds;
  const demand = data?.demand ?? [];

  if (isPending) return <div className="card">Loading history…</div>;
  if (isError) {
    return <div className="card">Couldn’t load history: {error instanceof Error ? error.message : String(error)}</div>;
  }

  return (
    <div>
      <p className="timezone-note">Times shown in {viewerTimeZone()}.</p>
      <FilterBar
        service={service}
        period={period}
        onServiceChange={setService}
        onPeriodChange={setPeriod}
      />
      <SummaryTiles demand={demand} holds={holds} />
      <LanesCallout demand={demand} />
      <div className="card">
        <h2>Demand by service</h2>
        <DemandTable demand={demand} />
      </div>
      <div className="card">
        <h2>Individual holds</h2>
        <PersonFilter person={person} people={people} onPersonChange={setPerson} />
        <HoldsTable holds={filteredHolds} />
      </div>
    </div>
  );
}
